import React from 'react';
import { MapPin, CheckCircle, ExternalLink, Home } from 'lucide-react';
import { dormitories } from '../data/mock';

const Dormitories = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Öğrenci Yurtları</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Prag'da güvenli, konforlu ve modern öğrenci yurtlarında kalın. Size en uygun konaklama seçeneğini bulmanıza yardımcı oluyoruz.
          </p>
        </div>
      </section>

      {/* Dormitories List */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {dormitories.map((dorm) => (
              <div
                key={dorm.id}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
              >
                <div className="h-72 overflow-hidden">
                  <img
                    src={dorm.image}
                    alt={dorm.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">{dorm.name}</h2>
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <MapPin size={18} className="text-amber-600" />
                        <span>{dorm.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Home size={18} className="text-amber-600" />
                        <span className="text-sm font-medium">{dorm.type}</span>
                      </div>
                    </div>
                    <div className="bg-amber-100 px-4 py-2 rounded-lg">
                      <div className="text-lg font-bold text-amber-900">{dorm.priceRange}</div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">{dorm.description}</p>

                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Özellikler:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {dorm.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <a
                      href={`https://${dorm.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors text-center flex items-center justify-center gap-2"
                    >
                      Web Sitesini Ziyaret Et
                      <ExternalLink size={18} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Student Dormitories */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Neden Öğrenci Yurdu?</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="text-amber-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ekonomik ve Pratik</h3>
                <p className="text-gray-600">Tüm faturalar dahil, öğrenci bütçesine uygun fiyatlar. Ayrıca ek masraflarla uğraşmazsınız.</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="text-amber-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sosyal Ortam</h3>
                <p className="text-gray-600">Dünyanın her yerinden öğrencilerle tanışın, yeni arkadaşlıklar kurun ve kültürel deneyim kazanın.</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="text-amber-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Merkezi Konum</h3>
                <p className="text-gray-600">Üniversitelere ve şehir merkezine yakın, toplu taşımaya kolay erişim.</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="text-amber-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Güvenlik</h3>
                <p className="text-gray-600">24/7 güvenlik, kamera sistemi ve resepsiyon hizmeti ile güvenli yaşam alanı.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Konaklama Konusunda Yardıma mı İhtiyacınız Var?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Size en uygun yurdu bulmak, rezervasyon yapmak ve yerleşim sürecinde rehberlik etmek için buradayız.
            </p>
            <a
              href="/iletisim"
              className="inline-block bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-lg"
            >
              Bizimle İletişime Geçin
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dormitories;
